import config from 'config';
import cookies from 'js-cookie';

import { neptune } from 'analytics/neptune';
import { backendClient } from '@neptune/shared/core-apis-backend-domain';

export abstract class AbApiExperiment<VariantType extends string> {
  protected abstract get backendKey(): string;

  protected abstract get cookieName(): string;

  protected abstract get ratio(): number;

  protected abstract get variantA(): VariantType;

  protected abstract get variantB(): VariantType;

  protected abstract get testDate(): string;

  protected abstract get test(): string;

  protected abstract get username(): string | undefined;

  public async initialize(): Promise<VariantType> {
    const { variantFromApi, variant } = await this.obtainVariant();
    await this.persistVariant(variant, variantFromApi);
    return variant;
  }

  protected async obtainVariant() {
    const variantFromApi = await this.getVariantFromApi();
    const variantFromCookie = this.getVariantFromCookie();
    const randomVariant = this.getRandomVariant();
    const variant = variantFromApi || variantFromCookie || randomVariant;
    return { variantFromApi, variantFromCookie, randomVariant, variant };
  }

  protected async persistVariant(variant: VariantType, variantFromApi?: VariantType) {
    if (!variantFromApi) {
      await this.setVariantToApi(variant);
    }

    this.setVariantToCookie(variant);
    this.track(variant);
  }

  protected async getVariantFromApi(): Promise<VariantType | undefined> {
    let variant: VariantType | undefined;

    if (this.username == null) {
      return;
    }

    try {
      const response = await backendClient.getScopedProperty({
        propertyType: 'USER',
        scope: this.username,
        key: this.backendKey,
      });
      variant = response.value as VariantType | undefined;
    } catch (e) {}

    return variant;
  }

  protected async setVariantToApi(variant: VariantType) {
    if (this.username == null) {
      return;
    }

    try {
      await backendClient.setScopedProperty({
        propertyType: 'USER',
        scope: this.username,
        propertyBody: { key: this.backendKey, value: variant },
      });
    } catch (e) {}
  }

  protected setVariantToCookie(value: VariantType) {
    cookies.set(this.cookieName, value, {
      domain: config.neptuneCookieDomain,
      path: '/',
      expires: 365,
    });
  }

  protected getVariantFromCookie(): VariantType | undefined {
    return cookies.get(this.cookieName) as VariantType | undefined;
  }

  protected getRandomVariant(): VariantType {
    const random = Math.random();
    return random > this.ratio ? this.variantB : this.variantA;
  }

  protected track(variant: VariantType) {
    neptune.track('being a/b tested', {
      test_date: this.testDate,
      test: this.test,
      variant,
    });
  }
}
